import React from 'react';
import { Box, Slider, Typography, Paper, alpha, useTheme } from '@mui/material';
import { format, parse } from 'date-fns';

const TimelineSlider = ({ versions, selectedVersion, onVersionChange }) => {
  const theme = useTheme();
  // Local drag value to keep UI smooth and avoid reloading tiles on every change
  const [dragValue, setDragValue] = React.useState(0);

  const marks = React.useMemo(() => {
    if (!versions) return [];
    return versions.map((version, index) => {
      const date = parse(version, 'ddMMyyyy', new Date());
      // Reverse the index so latest appears on the right
      const reversedIndex = versions.length - 1 - index;
      return {
        value: reversedIndex,
        label: format(date, 'dd MMM yyyy'),
      };
    });
  }, [versions]);

  const selectedValue = React.useMemo(() => {
    if (!versions) return 0;
    const index = versions.findIndex(v => v === selectedVersion);
    if (index === -1) return 0;
    // Reverse the index for slider position
    return versions.length - 1 - index;
  }, [selectedVersion, versions]);

  // Keep drag value in sync when selectedVersion changes from outside
  React.useEffect(() => {
    setDragValue(selectedValue);
  }, [selectedValue]);

  if (!versions || versions.length <= 1) {
    return null; // Don't render if there's one or zero versions
  }

  const handleSliderChange = (event, newValue) => {
    setDragValue(newValue);
  };

  const handleSliderChangeCommitted = (event, newValue) => {
    const sliderIndex = typeof newValue === 'number' ? newValue : dragValue;
    // Convert reversed slider index back to original array index
    const originalIndex = versions.length - 1 - sliderIndex;
    const newVersion = versions[originalIndex];
    if (newVersion && newVersion !== selectedVersion) {
      onVersionChange(newVersion);
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '50%',
        maxWidth: '600px',
        p: 2,
        borderRadius: '16px',
        backgroundColor: alpha(theme.palette.background.paper, 0.85),
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <Typography variant="subtitle2" align="center" gutterBottom>
        3D Model Timeline
        </Typography>
        <Box sx={{ p: '0 24px' }}>
          <Slider
            aria-label="3D Model Timeline"
            value={dragValue}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            step={null}
            marks={marks}
            min={0}
            max={marks.length - 1}
            valueLabelDisplay="off"
          />
        </Box>
      </Paper>
    );
};

export default TimelineSlider;